import { neon } from '@neondatabase/serverless';
import { Client, DBSnapshot, NewSnapshotData, WordPressConnection } from '../types';
import { EncryptionService } from '../src/utils/encryption';

// Type for database row
type Row = Record<string, any>;

// Type for query result
interface QueryResult<T = Row> {
  rows: T[];
  rowCount: number;
}

// Get the database URL from environment variables
const getDatabaseUrl = (): string => {
  // Check for Vite environment variable (browser)
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_NEONDB_URL) {
    return import.meta.env.VITE_NEONDB_URL as string;
  }
  
  throw new Error('VITE_NEONDB_URL environment variable is not set. Please check your .env file or environment configuration.');
};

let sql: any = null;

try {
  const databaseUrl = getDatabaseUrl();
  sql = neon(databaseUrl);
} catch (error) {
  console.error('Failed to initialize database connection:', error);
  // We'll throw the error when a database operation is attempted
}

// Simple query wrapper for consistent error handling
async function executeQuery<T extends Row = Row>(
  query: string, 
  params: any[] = []
): Promise<T[]> {
  try {
    console.log('Executing query:', { query, params });
    const start = Date.now();
    
    // For Neon serverless, we'll use a tagged template literal
    // Convert the query and params to a format that works with the Neon driver
    
    // Execute the query with parameters using template literals
    // This is a workaround for the Neon serverless driver's requirements
    const result = await sql.query(query, params);
    
    const duration = Date.now() - start;
    const rows = Array.isArray(result) ? result : [];
    
    console.log('Query executed', { query, duration, rows: rows.length });
    return rows as T[];
  } catch (error) {
    console.error('Database query error:', { query, error });
    throw error;
  }
}

// Test the database connection
export const testConnection = async (): Promise<boolean> => {
  try {
    const result = await executeQuery<{ test: number }>('SELECT 1 as test');
    console.log('Successfully connected to the database');
    return result.length > 0;
  } catch (error) {
    console.error('Error connecting to the database:', error);
    return false;
  }
};

// Query function with proper typing
export const query = async <T extends Row = Row>(
  text: string, 
  params: any[] = []
): Promise<QueryResult<T>> => {
  const rows = await executeQuery<T>(text, params);
  return { 
    rows, 
    rowCount: rows.length 
  };
};

// Type for plugin data
export interface Plugin {
  id: string;
  slug: string;
  name: string;
  author?: string;
  description?: string;
  created_at: Date;
  updated_at: Date;
}

// Type for plugin version data
export interface PluginVersion {
  id: string;
  plugin_id: string;
  version: string;
  download_url: string;
  requires_wp_version?: string;
  requires_php_version?: string;
  created_at: Date;
  updated_at: Date;
}

// Type for analysis queue item
export interface AnalysisQueueItem {
  id: string;
  plugin_id: string;
  plugin_version_id?: string;
  priority: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  error_message?: string;
  attempts: number;
  created_at: Date;
  started_at?: Date;
  completed_at?: Date;
}

// Type for Analysis Result data
export interface AnalysisResult {
  id: string; // UUID, usually generated by the DB
  plugin_version_id: string; // UUID
  analysis_date?: Date; // Defaults to CURRENT_TIMESTAMP
  php_compatibility?: any; // JSONB
  wp_compatibility?: any; // JSONB
  security_issues?: any; // JSONB
  performance_metrics?: any; // JSONB
  code_quality_score?: number; // FLOAT
  has_readme?: boolean;
  has_changelog?: boolean;
  has_license?: boolean;
  total_files?: number;
  total_lines?: number;
  total_functions?: number;
  total_classes?: number;
  total_hooks?: number;
  total_shortcodes?: number;
  analysis_summary?: string; // TEXT
  is_successful?: boolean; // Defaults to true
  error_message?: string; // TEXT
}

// Type for data to be inserted (id and analysis_date are optional)
export interface AnalysisResultInput {
  plugin_version_id: string;
  php_compatibility?: any;
  wp_compatibility?: any;
  security_issues?: any;
  performance_metrics?: any;
  code_quality_score?: number;
  has_readme?: boolean;
  has_changelog?: boolean;
  has_license?: boolean;
  total_files?: number;
  total_lines?: number;
  total_functions?: number;
  total_classes?: number;
  total_hooks?: number;
  total_shortcodes?: number;
  analysis_summary?: string;
  is_successful?: boolean;
  error_message?: string;
}

// Plugin-related functions
export const pluginService = {
  // Create a new plugin
  create: async (pluginData: {
    slug: string;
    name: string;
    author?: string;
    description?: string;
  }) => {
    const { slug, name, author, description } = pluginData;
    const result = await query(
      `INSERT INTO plugins (slug, name, author, description) 
       VALUES ($1, $2, $3, $4) 
       RETURNING *`,
      [slug, name, author, description]
    );
    return result.rows[0];
  },

  // Find a plugin by slug
  findBySlug: async (slug: string) => {
    const result = await query('SELECT * FROM plugins WHERE slug = $1', [slug]);
    return result.rows[0] || null;
  },

  // Get all plugins
  getAll: async () => {
    const result = await query('SELECT * FROM plugins ORDER BY name');
    return result.rows;
  },
  
  // Get plugin with versions
  getWithVersions: async (pluginId: string) => {
    const pluginResult = await query('SELECT * FROM plugins WHERE id = $1', [pluginId]);
    if (!pluginResult.rows.length) return null;
    
    const versionsResult = await query(
      'SELECT * FROM plugin_versions WHERE plugin_id = $1 ORDER BY created_at DESC',
      [pluginId]
    );
    
    return {
      ...pluginResult.rows[0],
      versions: versionsResult.rows
    };
  },

  // Add a plugin version
  addVersion: async (pluginId: string, versionData: {
    version: string;
    downloadUrl: string;
    requiresWpVersion?: string;
    requiresPhpVersion?: string;
  }) => {
    const { version, downloadUrl, requiresWpVersion, requiresPhpVersion } = versionData;
    const result = await query(
      `INSERT INTO plugin_versions 
       (plugin_id, version, download_url, requires_wp_version, requires_php_version)
       VALUES ($1, $2, $3, $4, $5) 
       RETURNING *`,
      [pluginId, version, downloadUrl, requiresWpVersion, requiresPhpVersion]
    );
    return result.rows[0];
  },

  // Add to analysis queue
  addToQueue: async (pluginId: string, versionId?: string, priority = 10) => {
    const result = await query(
      `INSERT INTO analysis_queue 
       (plugin_id, plugin_version_id, priority, status)
       VALUES ($1, $2, $3, 'pending') 
       RETURNING *`,
      [pluginId, versionId, priority]
    );
    return result.rows[0];
  },

  // Get next items from queue
  getNextFromQueue: async (limit = 5) => {
    const result = await query(
      `SELECT * FROM analysis_queue 
       WHERE status = 'pending' 
       ORDER BY priority DESC, created_at ASC 
       LIMIT $1`,
      [limit]
    );
    return result.rows;
  },
  
  // Update queue item status
  updateQueueStatus: async (id: string, status: string, error: string | null = null) => {
    const updates: string[] = ['status = $1'];
    const params: any[] = [status, id];
    
    let paramIndex = 3;
    
    if (status === 'processing') {
      updates.push(`started_at = NOW()`);
    } else if (['completed', 'failed'].includes(status)) {
      updates.push(`completed_at = NOW()`);
      
      if (error) {
        updates.push(`error_message = $${paramIndex++}`);
        params.push(error);
      }
    }
    
    if (status === 'failed') {
      updates.push(`attempts = attempts + 1`);
    }
    
    const queryStr = `
      UPDATE analysis_queue 
      SET ${updates.join(', ')}
      WHERE id = $2
      RETURNING *
    `;
    
    const result = await query(queryStr, params);
    return result.rows[0];
  },

  // Save analysis results
  saveAnalysisResults: async (data: AnalysisResultInput): Promise<AnalysisResult> => {
    const {
      plugin_version_id,
      php_compatibility,
      wp_compatibility,
      security_issues,
      performance_metrics,
      code_quality_score = null,
      has_readme = false, // Default from schema
      has_changelog = false, // Default from schema
      has_license = false, // Default from schema
      total_files = 0, // Default from schema
      total_lines = 0, // Default from schema
      total_functions = 0, // Default from schema
      total_classes = 0, // Default from schema
      total_hooks = 0, // Default from schema
      total_shortcodes = 0, // Default from schema
      analysis_summary = null,
      is_successful = true, // Default from schema
      error_message = null,
    } = data;

    // Ensure JSONB fields are stringified
    const params = [
      plugin_version_id,
      php_compatibility ? JSON.stringify(php_compatibility) : null,
      wp_compatibility ? JSON.stringify(wp_compatibility) : null,
      security_issues ? JSON.stringify(security_issues) : null,
      performance_metrics ? JSON.stringify(performance_metrics) : null,
      code_quality_score,
      has_readme,
      has_changelog,
      has_license,
      total_files,
      total_lines,
      total_functions,
      total_classes,
      total_hooks,
      total_shortcodes,
      analysis_summary,
      is_successful,
      error_message,
    ];

    const sqlQuery = `
      INSERT INTO analysis_results (
        plugin_version_id, php_compatibility, wp_compatibility, security_issues,
        performance_metrics, code_quality_score, has_readme, has_changelog,
        has_license, total_files, total_lines, total_functions, total_classes,
        total_hooks, total_shortcodes, analysis_summary, is_successful, error_message
      ) VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9, $10, $11, $12, $13, $14, $15, $16, $17, $18)
      RETURNING *;
    `;
    
    const result = await query<AnalysisResult>(sqlQuery, params);
    return result.rows[0];
  }
};

// Client-related functions
export const clientService = {
  create: async (name: string): Promise<Client> => {
    const result = await query<Client>(
      `INSERT INTO clients (name)
       VALUES ($1)
       RETURNING id, name, created_at, updated_at`,
      [name]
    );
    return result.rows[0];
  },

  findByName: async (name: string): Promise<Client | null> => {
    const result = await query<Client>('SELECT id, name, created_at, updated_at FROM clients WHERE name = $1', [name]);
    return result.rows[0] || null;
  },

  getAll: async (): Promise<Client[]> => {
    const result = await query<Client>('SELECT id, name, created_at, updated_at FROM clients ORDER BY name');
    return result.rows;
  },

  deleteById: async (id: string): Promise<void> => {
    try {
      // The ON DELETE CASCADE constraint in the database schema will handle deletion
      // of related plugin_snapshots and wordpress_connections.
      await query(
        `DELETE FROM clients WHERE id = $1`,
        [id]
      );
      // As with snapshot deletion, rowCount could be checked here if needed,
      // but a DELETE on a non-existent ID is not an SQL error.
    } catch (error) {
      console.error(`Failed to delete client with id ${id}:`, error);
      // Re-throw or throw a more specific error
      throw new Error(`Database error: Failed to delete client ${id}.`);
    }
  },
};

// Snapshot-related functions
export const snapshotService = {
  create: async (snapshotData: NewSnapshotData): Promise<DBSnapshot> => {
    const { client_id, timestamp, plugins, raw_input } = snapshotData;
    // Ensure timestamp is in ISO format for PostgreSQL
    const isoTimestamp = timestamp instanceof Date ? timestamp.toISOString() : timestamp;
    // Ensure plugins array is stringified for JSONB
    const pluginsJson = JSON.stringify(plugins);

    const result = await query<DBSnapshot>(
      `INSERT INTO plugin_snapshots (client_id, timestamp, plugins, raw_input)
       VALUES ($1, $2, $3, $4)
       RETURNING id, client_id, timestamp, plugins, raw_input, created_at`,
      [client_id, isoTimestamp, pluginsJson, raw_input]
    );
    return result.rows[0];
  },

  getByClientId: async (clientId: string): Promise<DBSnapshot[]> => {
    const result = await query<DBSnapshot>(
      `SELECT id, client_id, timestamp, plugins, raw_input, created_at
       FROM plugin_snapshots
       WHERE client_id = $1
       ORDER BY timestamp DESC`,
      [clientId]
    );
    return result.rows;
  },

  deleteById: async (id: string): Promise<boolean> => {
    try {
      const result = await query(
        `DELETE FROM plugin_snapshots WHERE id = $1`,
        [id]
      );
      // Return true if a row was deleted, false otherwise
      return result.rowCount > 0;
    } catch (error) {
      console.error(`Failed to delete snapshot with id ${id}:`, error);
      // Re-throw or throw a more specific error
      throw new Error(`Database error: Failed to delete snapshot ${id}.`);
    }
  },
};

export default {
  query,
  testConnection,
  plugin: pluginService,
  client: clientService,
  snapshot: snapshotService,
  // WordPress Connection-related functions
  wordPressConnection: {
    save: async (connection: WordPressConnection): Promise<WordPressConnection> => {
      const { client_id, wordpress_url, api_key, api_secret, is_connected, last_tested } = connection;
      
      // Safely handle last_tested which could be Date, string, or null/undefined
      let lastTestedIso: string | null = null;
      if (last_tested !== null && last_tested !== undefined) {
        // Use type predicate to check if the value is a Date-like object
        const isDateLike = (value: unknown): value is { toISOString: () => string } => {
          return typeof value === 'object' && value !== null && 'toISOString' in value && 
                 typeof (value as any).toISOString === 'function';
        };
        
        if (isDateLike(last_tested)) {
          lastTestedIso = last_tested.toISOString();
        } else if (typeof last_tested === 'string') {
          lastTestedIso = last_tested;
        }
      }

      // Encrypt the API secret if it's not empty and not already encrypted
      let encryptedSecret = api_secret;
      if (api_secret && !EncryptionService.isEncrypted(api_secret)) {
        try {
          encryptedSecret = await EncryptionService.encrypt(api_secret);
        } catch (error) {
          console.error('Failed to encrypt API secret:', error);
          throw new Error('Failed to encrypt API secret');
        }
      }

      const result = await query<WordPressConnection>(
        `INSERT INTO wordpress_connections (client_id, wordpress_url, api_key, api_secret, is_connected, last_tested)
         VALUES ($1, $2, $3, $4, $5, $6)
         ON CONFLICT (client_id) 
         DO UPDATE SET
           wordpress_url = EXCLUDED.wordpress_url,
           api_key = EXCLUDED.api_key,
           api_secret = EXCLUDED.api_secret,
           is_connected = EXCLUDED.is_connected,
           last_tested = EXCLUDED.last_tested,
           updated_at = CURRENT_TIMESTAMP
         RETURNING id, client_id, wordpress_url, api_key, api_secret, is_connected, last_tested, created_at, updated_at`,
        [client_id, wordpress_url, api_key, encryptedSecret, is_connected ?? false, lastTestedIso]
      );
      
      if (!result.rows[0]) {
        throw new Error('Failed to save WordPress connection');
      }
      return result.rows[0];
    },

    getByClientId: async (clientId: string): Promise<WordPressConnection | null> => {
      const result = await query<WordPressConnection>(
        'SELECT id, client_id, wordpress_url, api_key, api_secret, is_connected, last_tested, created_at, updated_at FROM wordpress_connections WHERE client_id = $1',
        [clientId]
      );
      
      if (!result.rows[0]) {
        return null;
      }
      
      const connection = result.rows[0];
      
      // Decrypt the API secret if it exists and is encrypted
      if (connection.api_secret && EncryptionService.isEncrypted(connection.api_secret)) {
        try {
          connection.api_secret = await EncryptionService.decrypt(connection.api_secret);
        } catch (error) {
          console.error('Failed to decrypt API secret:', error);
          // Don't fail the whole operation, just leave it encrypted
        }
      }
      
      return connection;
    },
    
    deleteByClientId: async (clientId: string): Promise<void> => {
      await query(
        'DELETE FROM wordpress_connections WHERE client_id = $1',
        [clientId]
      );
    }
  }
};